import { Component, type ErrorInfo, type ReactNode } from 'react'

import { Button } from '../components/Button.js'
import styles from './AppShell.module.css'

interface Props {
  readonly children: ReactNode
}

interface State {
  readonly error: Error | null
}

/**
 * Catches a render error so one broken screen does not blank the app.
 *
 * Still a class component: React provides no hook equivalent of
 * `componentDidCatch`, and there is no third-party option here because nothing
 * in this app may phone home (ADR-0007 — no analytics, no error reporting, no
 * network at runtime). The error goes to the console and onto the screen, and
 * that is the whole of it.
 *
 * The recovery offered is deliberately *not* a reload. The user's data is in
 * IndexedDB and survives, but an entry half-filled in memory does not — so the
 * first offer is to return to a working screen with the app still running.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled render error', error, info.componentStack)
  }

  override render() {
    const { error } = this.state
    if (!error) {
      return this.props.children
    }

    return (
      <div className={styles['notice']} role="alert">
        <h1>Something broke on this screen</h1>
        <p>
          Your data is stored on this device and is unaffected — nothing has been lost.
          Anything you were part-way through typing has not been saved.
        </p>
        <pre className={styles['noticeDetail']}>{error.message}</pre>
        <Button variant="primary" onClick={() => this.setState({ error: null })}>
          Try again
        </Button>
      </div>
    )
  }
}
